import React, { useEffect, TouchEvent } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { ReactComponent as DesktopHoverIcon } from "./DesktopHoverIcon.svg";
import { ReactComponent as MobileHoverIcon } from "./MobileHoverIcon.svg";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {isMobile} from 'react-device-detect';
import { getStorageValue } from "../../../../utils/localStorageState";


const Slider: React.FC = () => {
  const wrapperRef = React.createRef<any>();
  const maskImageRef = React.createRef<any>();
  const desktopHoverImageRef = React.createRef<any>();
  const mobileHoverImageRef = React.createRef<any>();
  const langCode = getStorageValue("language", "hr");

  const theme = useTheme();
  const smBreakPoint = useMediaQuery(theme.breakpoints.up("sm"));

  const WRAPPER_HEIGHT = smBreakPoint ? 680 : 480;

  const homePageData = useSelector((state: RootState) => state.homePage.data);
  const isFetching = useSelector(
    (state: RootState) => state.homePage.isFetching
  );

  const initialPosition = React.useCallback(() => {
    if (maskImageRef?.current && wrapperRef?.current) {
      const { clientWidth } = wrapperRef.current;
      maskImageRef.current.style.transform = `translate(${
        clientWidth * 0.5 - 10
      }px, ${WRAPPER_HEIGHT * 0.5 - 10}px)`;
    }

    if (desktopHoverImageRef?.current) {
      desktopHoverImageRef.current.style.left = "50%";
      desktopHoverImageRef.current.style.top = "50%";
      desktopHoverImageRef.current.style.transform = "translate(-50%, -50%)";
    }
    if (mobileHoverImageRef?.current) {
      mobileHoverImageRef.current.style.left = "50%";
      mobileHoverImageRef.current.style.top = "50%";
      mobileHoverImageRef.current.style.transform = "translate(-50%, -52.5%)";
    }
  }, [desktopHoverImageRef, maskImageRef, mobileHoverImageRef, wrapperRef]);

  useEffect(() => {
    // Make hover image center initially
    initialPosition();
  }, [initialPosition]);

  if (isFetching) {
    return (
      <Stack position="relative" mt={2}>
        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height={WRAPPER_HEIGHT}
        />
        <Stack
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: smBreakPoint ? "60%" : "90%",
          }}
        >
          <Typography variant={smBreakPoint ? "h2" : "h4"}>
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
          </Typography>
          <Typography variant={smBreakPoint ? "h5" : "h6"} mx={8}>
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const handleMouseMove = (evt: any) => {
    const containerWidth = 1152;

    let marginLeft: number = 0;
    const widthDiff = window.innerWidth - containerWidth;

    if (widthDiff > 0) {
      marginLeft = widthDiff * 0.5;
    }

    if (desktopHoverImageRef && desktopHoverImageRef.current) {
      maskImageRef.current.style.transform = `translate(${
        evt.pageX - 20 - marginLeft
      }px, ${evt.pageY - 50}px)`;

      desktopHoverImageRef.current.style.left = "0%";
      desktopHoverImageRef.current.style.top = "0%";
      desktopHoverImageRef.current.style.transform = `translate(${
        evt.pageX - 190 - marginLeft
      }px, ${evt.pageY - 380}px)`;
    }
  };

  const handleTouchMove = (evt: TouchEvent<HTMLDivElement>) => {
    const containerWidth = 1152;

    let marginLeft: number = 0;
    const widthDiff = window.innerWidth - containerWidth;

    if (widthDiff > 0) {
      marginLeft = widthDiff * 0.5;
    }

    if (mobileHoverImageRef && mobileHoverImageRef.current) {
      maskImageRef.current.style.transform = `translate(${
        evt.touches[0].clientX - 20 - marginLeft
      }px, ${WRAPPER_HEIGHT * 0.5 - 10}px)`;

      mobileHoverImageRef.current.style.left = "0%";
      mobileHoverImageRef.current.style.top = "50%";
      mobileHoverImageRef.current.style.transform = `translate(${
        evt.touches[0].clientX - (window.innerWidth > 600 ? 190 : 110)
      }px, -52.5%)`;
    }
  }

  const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;

  return (
    <Box
      onMouseMove={(ev) => handleMouseMove(ev)}
      onMouseLeave={() => initialPosition()}
      onTouchMove={(ev) => handleTouchMove(ev)}
      onTouchEnd={() => initialPosition()}
      sx={{ position: "relative", overflow: "hidden" }}
      ref={wrapperRef}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: WRAPPER_HEIGHT,
          borderTop: "3px solid rgba(0, 0, 0, 0.01)",
          marginTop: "-46px",
        }}
      >
        <Box
          ref={maskImageRef}
          sx={{
            position: "absolute",
            pointerEvents: "none",
            width: "200vw",
            height: "200%",
            left: "-100vw",
            top: "-100%",
            transformOrigin: "center",
            backdropFilter: `blur(${isFirefox ? 0 : 6}px)`,
            WebkitBackdropFilter: "blur(6px)",
            WebkitMaskImage: `radial-gradient(${
              smBreakPoint ? "150px" : "80px"
            } ${
              smBreakPoint ? "200px" : "100px"
            } at 50% 50% , transparent 99%, black 100%)`,
            backgroundColor: isFirefox ? "rgba(255, 255, 255, 0.92)" : "rgba(255, 255, 255, 0.05)",
          }}
        />
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant={smBreakPoint ? "h2" : "h4"}
            fontWeight={700}
            gutterBottom
            color={'#96bb55'}
            maxWidth={smBreakPoint ? 550 : 350}
            fontSize={smBreakPoint ? undefined : '1.25rem'}
          >
            {'hr' === langCode ? homePageData.data?.attributes?.mainSliderText : homePageData.data?.attributes?.mainSliderTextEn}
          </Typography>
          <Typography
              variant={smBreakPoint ? "h5" : "h6"}
              maxWidth={smBreakPoint ? 550 : 350}
              fontSize={smBreakPoint ? undefined : '1rem'}
          >
            {'hr' === langCode ? homePageData.data?.attributes?.sliderSubText : homePageData.data?.attributes?.sliderSubTextEn}
          </Typography>
        </Box>
      </Box>
      <Box
        ref={false === isMobile ? desktopHoverImageRef : mobileHoverImageRef}
        sx={{
          position: "absolute",
        }}
      >
        {smBreakPoint ? <DesktopHoverIcon /> : <MobileHoverIcon />}
      </Box>
    </Box>
  );
};

export default Slider;
